import React, { createContext, ReactNode, useContext, useReducer } from 'react';
import { LoanTerms } from '../../common/types';
import { monthlyMortgagePayments } from '../../mortrage-lib/monthlyMortgagePayments';
import {
  DownPaymentContextType,
  DownPaymentDataActions,
  DownPaymentDataActionsTypes,
  DownPaymentState
} from '../types';

const initialState: DownPaymentState = {
  downPayment: 20000,
  downPaymentPercent: 20,
  interestRate: 6.625,
  loanTerm: LoanTerms.THIRTY,
  showFull: false
};

const LoanTermToNumber = {
  [LoanTerms.THIRTY]: 30,
  [LoanTerms.TWENTY]: 20,
  [LoanTerms.FIFTEEN]: 15
};

function calculatorReducer(state: DownPaymentState, action: DownPaymentDataActions) {
  switch (action.type) {
    case DownPaymentDataActionsTypes.SET_DOWN_PAYMENT:
      return { ...state, downPayment: action.payload };
    case DownPaymentDataActionsTypes.SET_DOWN_PAYMENT_PERCENT:
      return { ...state, downPaymentPercent: action.payload };
    case DownPaymentDataActionsTypes.SET_INTEREST_RATE:
      return { ...state, interestRate: action.payload };
    case DownPaymentDataActionsTypes.SET_LOAN_TERM:
      return { ...state, loanTerm: action.payload };
    case DownPaymentDataActionsTypes.SET_SHOW_FULL:
      return { ...state, showFull: action.payload };
    default:
      throw new Error(`Unhandled action type`);
  }
}

const DownPaymentContext = createContext<DownPaymentContextType>({
  state: initialState,
  dispatch: () => null,
  getHomePrice: () => 0,
  getMonthlyPayment: () => 0
});

export function DownPaymentProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(calculatorReducer, initialState);

  const getHomePrice = () => (state.downPayment / state.downPaymentPercent) * 100;
  const getMonthlyPayment = () => {
    const calculationsResult = monthlyMortgagePayments({
      loanAmount: getHomePrice(),
      interestRate: state.interestRate,
      termInYears: LoanTermToNumber[state.loanTerm]
    });
    if (typeof calculationsResult === 'number') {
      return calculationsResult;
    } else {
      return 0;
    }
  };

  const value = {
    state,
    dispatch,
    getHomePrice,
    getMonthlyPayment
  };

  return <DownPaymentContext.Provider value={value}>{children}</DownPaymentContext.Provider>;
}

export function useDownPayment() {
  const context = useContext(DownPaymentContext);
  if (context === undefined) {
    throw new Error('useDownPayment must be used within a DownPaymentProvider');
  }
  return context;
}
